import React from 'react'
import { Link } from 'react-router-dom'
import { Button } from '@blueprintjs/core/lib/cjs/components/button/buttons'
import { Menu } from '@blueprintjs/core/lib/cjs/components/menu/menu'
import { MenuItem } from '@blueprintjs/core/lib/cjs/components/menu/menuItem'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEllipsisVertical } from '@fortawesome/pro-light-svg-icons/faEllipsisVertical'
import { faArrowDown } from '@fortawesome/pro-solid-svg-icons/faArrowDown'
import { faArrowUp } from '@fortawesome/pro-solid-svg-icons/faArrowUp'
import moment from 'moment'
import IconMenu from 'src/components/IconMenu'
import { FILTER_ANY_VALUE, ORGANIZATION_SCOPE_ALL_LABEL } from './utils'
import styles from './runbooksTemplates.module.scss'

export const filterConfig = Object.freeze({
    templateType: Object.freeze([
        FILTER_ANY_VALUE,
        'runbook_framework',
        'action_group',
    ]),
    organization: Object.freeze([
        FILTER_ANY_VALUE,
        ORGANIZATION_SCOPE_ALL_LABEL,
    ]),
    runbookCategory: Object.freeze([
        FILTER_ANY_VALUE,
        'Cybersecurity',
        'Failover',
        'Restart',
        ORGANIZATION_SCOPE_ALL_LABEL,
    ]),
    deviceType: Object.freeze([
        FILTER_ANY_VALUE,
        'Server',
        'Network',
        'Storage',
        'WP',
        ORGANIZATION_SCOPE_ALL_LABEL,
    ]),
})

/**
 * Renders the Template Name cell as a link.
 * @param {function} onTemplateLinkClick - Callback for link click.
 * @returns {function}
 */
const renderTemplateNameCell = (onTemplateLinkClick) =>
    function TemplateNameCell({ cell, row: runbook }) {
        return (
            <Link
                to="#"
                className={styles.templateLink}
                onClick={(event) => onTemplateLinkClick(event, runbook)}
            >
                {cell}
            </Link>
        )
    }

/**
 * Renders a timestamp cell in 'DD/MM/YY - HH:mm' format.
 * @param {{cell: string}} param0
 * @returns {string}
 */
const renderTimestampCell = ({ cell }) => {
    if (!cell) {
        return ''
    }
    return moment(cell).format('DD/MM/YY - HH:mm')
}

/**
 * Renders the priority cell with swap up/down controls.
 * @param {object} params
 * @param {function} params.onSwapTemplateUp
 * @param {function} params.onSwapTemplateDown
 * @param {boolean} params.isPrioritySwapDisabled
 * @param {boolean} params.isSwappingPriority
 * @param {number} params.currentPage
 * @param {number} params.totalPages
 * @returns {JSX.Element}
 */
const renderPriorityCell = ({
    onSwapTemplateUp,
    onSwapTemplateDown,
    isPrioritySwapDisabled,
    isSwappingPriority,
    currentPage,
    totalPages,
}) =>
    function PriorityCell({ row, rowIndex, rowCount }) {
        const canSwapUp = rowIndex > 0 || currentPage > 1
        const canSwapDown = rowIndex < rowCount - 1 || currentPage < totalPages
        const isDisabled = isPrioritySwapDisabled || isSwappingPriority

        return (
            <span className={styles.priorityCell}>
                {canSwapUp && (
                    <Button
                        minimal
                        className={styles.priorityButton}
                        onClick={() => onSwapTemplateUp(row)}
                        disabled={isDisabled}
                        aria-label="Swap template up"
                        title="Swap up"
                    >
                        <FontAwesomeIcon
                            icon={faArrowUp}
                            className={styles.priorityArrow}
                        />
                    </Button>
                )}

                {canSwapDown && (
                    <Button
                        minimal
                        className={styles.priorityButton}
                        onClick={() => onSwapTemplateDown(row)}
                        disabled={isDisabled}
                        aria-label="Swap template down"
                        title="Swap down"
                    >
                        <FontAwesomeIcon
                            icon={faArrowDown}
                            className={styles.priorityArrow}
                        />
                    </Button>
                )}
            </span>
        )
    }

/**
 * Renders the organization cell with fallback to scope when name is missing.
 * @param {{cell: string, row: object}} param0
 * @returns {string}
 */
const renderOrganizationCell = ({ cell, row }) => {
    return row.organization_name || cell || ''
}

/**
 * Renders the menu cell with edit/delete actions.
 * @param {function} onEditTemplate - Edit callback.
 * @param {function} onDeleteTemplate - Delete callback.
 * @returns {function}
 */
const renderMenuCell = (onEditTemplate, onDeleteTemplate) =>
    function MenuCell({ row: runbook }) {
        return (
            <IconMenu
                icon={
                    <FontAwesomeIcon
                        icon={faEllipsisVertical}
                        className={styles.rowIconMenu}
                    />
                }
                className={styles.rowMenu}
                content={
                    <Menu>
                        <MenuItem
                            text="Edit template"
                            icon="edit"
                            onClick={() => onEditTemplate(runbook)}
                        />
                        <MenuItem
                            text="Delete template"
                            icon="trash"
                            onClick={() => onDeleteTemplate(runbook)}
                        />
                    </Menu>
                }
            />
        )
    }

/**
 * Returns the table configuration for the RunbooksTemplates table.
 * @param {object} params
 * @param {object} params.actions
 * @param {function} params.actions.onDeleteTemplate
 * @param {function} params.actions.onEditTemplate
 * @param {function} params.actions.onTemplateLinkClick
 * @param {function} params.actions.onSwapTemplateUp
 * @param {function} params.actions.onSwapTemplateDown
 * @param {object} params.swapState
 * @param {boolean} params.swapState.isPrioritySwapDisabled
 * @param {boolean} params.swapState.isSwappingPriority
 * @param {object} params.pagination
 * @param {number} params.pagination.currentPage
 * @param {number} params.pagination.totalPages
 * @returns {Array}
 */
const getTableConfig = ({ actions, swapState, pagination }) => {
    const {
        onDeleteTemplate,
        onEditTemplate,
        onTemplateLinkClick,
        onSwapTemplateUp,
        onSwapTemplateDown,
    } = actions
    const { isPrioritySwapDisabled, isSwappingPriority } = swapState
    const { currentPage, totalPages } = pagination

    return [
        {
            id: 'id',
            name: 'ID',
            accessor: 'id',
            width: '5%',
            sortable: true,
        },
        {
            id: 'template-name',
            name: 'Template Name',
            accessor: 'name',
            width: '15%',
            sortable: true,
            cell: renderTemplateNameCell(onTemplateLinkClick),
        },
        {
            id: 'template-type',
            name: 'Template Type',
            accessor: 'template_type',
            width: '12%',
            sortable: true,
        },
        {
            id: 'organization',
            name: 'Organization',
            accessor: 'organization_scope',
            width: '10%',
            sortable: true,
            sortColumn: 'organization__name',
            cell: renderOrganizationCell,
        },
        {
            id: 'runbook-category',
            name: 'Runbook Category',
            accessor: 'runbook_category',
            width: '10%',
            sortable: true,
        },
        {
            id: 'device-type',
            name: 'Device Type',
            accessor: 'device_type',
            width: '10%',
            sortable: true,
        },
        {
            id: 'created-at',
            name: 'Created At',
            accessor: 'created_at',
            width: '10%',
            sortable: true,
            cell: renderTimestampCell,
        },
        {
            id: 'last-updated',
            name: 'Last Updated',
            accessor: 'updated_at',
            width: '10%',
            sortable: true,
            cell: renderTimestampCell,
        },
        {
            id: 'updated-by',
            name: 'Updated By',
            accessor: 'updated_by_name',
            width: '10%',
            sortable: true,
        },
        {
            id: 'priority',
            name: 'Priority',
            accessor: 'priority',
            width: '5%',
            cell: renderPriorityCell({
                onSwapTemplateUp,
                onSwapTemplateDown,
                isPrioritySwapDisabled,
                isSwappingPriority,
                currentPage,
                totalPages,
            }),
        },
        {
            id: 'menu-icon',
            width: '3%',
            cell: renderMenuCell(onEditTemplate, onDeleteTemplate),
        },
    ]
}

export default getTableConfig
